import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Alert,
  Stack,
  Paper,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import PlayerNameCell from "../PlayerNameCell";
import {
  QUERY_PAGE_SIZE_OPTIONS,
  buildQueryResultRows,
  formatQueryResultsCaption,
  formatQueryStatValue,
  getGridSortModel,
  getQueryDisplayStats,
  getStatAbbrev,
  getStatLabel,
} from "../../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  getResultsGridSx,
  sortedClassFor,
} from "../../utils/resultsGrid";
import { filterRowsByPlayerName } from "../../utils/filterRowsByPlayerName";
import {
  collectPlayerIds,
  getHeadshotUrl,
} from "../../utils/playerHeadshots";
import { usePlayerHeadshots } from "../../hooks/usePlayerHeadshots";

const LOADED_NAME_SEARCH_EMPTY = "No loaded players match that name.";

const QueryResultsPanel = ({
  queryParams,
  queryResults,
  isExecuting,
  queryError,
  catalog,
  onPlayerClick,
  onSortModelChange,
  onLimitChange,
}) => {
  const [nameSearch, setNameSearch] = useState("");
  const isTeamQuery = queryParams.queryType === "team";
  const sport = catalog?.sport || "mlb";
  const showNameSearch = !isTeamQuery;
  const searchResetKey = [
    queryParams.queryType,
    queryParams.groupId,
    queryParams.startYear,
    queryParams.endYear,
    JSON.stringify(queryParams.statisticalFilters || {}),
    JSON.stringify(queryParams.teams || []),
    JSON.stringify(queryParams.biographicalFilters || {}),
  ].join("|");

  useEffect(() => {
    setNameSearch("");
  }, [searchResetKey]);

  const sortModel = useMemo(
    () => (isTeamQuery ? undefined : getGridSortModel(queryParams)),
    [isTeamQuery, queryParams]
  );
  const sortedField = isTeamQuery ? "playerCount" : sortModel?.[0]?.field;
  const pageSizeOptions = QUERY_PAGE_SIZE_OPTIONS.includes(queryParams.limit)
    ? QUERY_PAGE_SIZE_OPTIONS
    : [...QUERY_PAGE_SIZE_OPTIONS, queryParams.limit].sort((a, b) => a - b);

  const rows = useMemo(
    () => buildQueryResultRows(queryParams, queryResults?.results, catalog),
    [catalog, queryParams, queryResults]
  );
  const playerIds = useMemo(
    () => (isTeamQuery ? [] : collectPlayerIds(rows)),
    [isTeamQuery, rows]
  );
  const { headshots } = usePlayerHeadshots(playerIds, { sport });
  const visibleRows = useMemo(
    () => (showNameSearch ? filterRowsByPlayerName(rows, nameSearch) : rows),
    [nameSearch, rows, showNameSearch]
  );

  const columns = useMemo(() => {
    if (isTeamQuery) {
      return [
        {
          field: "rank",
          headerName: "#",
          width: 56,
          sortable: false,
          align: "right",
          headerAlign: "right",
        },
        {
          field: "team",
          headerName: "Team",
          flex: 1.2,
          minWidth: 160,
          headerClassName: sortedClassFor("team", sortedField),
          cellClassName: sortedClassFor("team", sortedField),
        },
        {
          field: "playerCount",
          headerName: "Players",
          width: 120,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("playerCount", sortedField),
          cellClassName: sortedClassFor("playerCount", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
        {
          field: "seasonCount",
          headerName: "Seasons",
          width: 120,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("seasonCount", sortedField),
          cellClassName: sortedClassFor("seasonCount", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
      ];
    }

    const base = [
      {
        field: "rank",
        headerName: "#",
        width: 56,
        sortable: false,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 200,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            key={params.row.id}
            playerId={params.row.playerId}
            name={params.value}
            photoUrl={getHeadshotUrl(headshots, params.row.playerId)}
            sport={sport}
            onPlayerClick={onPlayerClick}
          />
        ),
      },
    ];

    if (queryParams.queryType === "season") {
      base.push(
        {
          field: "year",
          headerName: "Year",
          width: 90,
          headerClassName: sortedClassFor("year", sortedField),
          cellClassName: sortedClassFor("year", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
        {
          field: "team",
          headerName: "Team",
          width: 120,
          headerClassName: sortedClassFor("team", sortedField),
          cellClassName: sortedClassFor("team", sortedField),
          sortingOrder: NAME_SORT_ORDER,
        }
      );
    }

    getQueryDisplayStats(queryParams, catalog).forEach((stat) => {
      base.push({
        field: stat,
        headerName: getStatAbbrev(stat, catalog),
        description: getStatLabel(stat, catalog),
        width: 100,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor(stat, sortedField),
        cellClassName: sortedClassFor(stat, sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) => formatQueryStatValue(stat, params.value, catalog),
      });
    });

    return base;
  }, [catalog, headshots, isTeamQuery, onPlayerClick, queryParams, sortedField, sport]);

  const hasLoaded = Boolean(queryResults);
  const isEmpty = hasLoaded && !isExecuting && queryResults.results?.length === 0;

  if (queryError && !queryResults?.results?.length) {
    return (
      <Alert severity="error">
        {queryError?.data?.error || queryError?.message || "Query failed"}
      </Alert>
    );
  }

  if (isEmpty) {
    return (
      <Paper
        sx={{
          p: 6,
          minHeight: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1.5,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "text.disabled" }} />
        <Typography variant="h6" fontWeight={700}>
          {isTeamQuery
            ? "No teams match these filters"
            : "No players match these filters"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
          Clear a chip or open Filters to adjust.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasLoaded
              ? formatQueryResultsCaption(queryParams, queryResults.totalResults)
              : queryParams.sortBy === "homeruns"
                ? "Career home run leaders"
                : "Loading players..."}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {showNameSearch ? (
            <TextField
              label="Search results"
              variant="outlined"
              size="small"
              value={nameSearch}
              onChange={(event) => setNameSearch(event.target.value)}
              placeholder="Player name"
              sx={{ minWidth: 180, maxWidth: 260 }}
            />
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Show
          </Typography>
          <Select
            size="small"
            value={queryParams.limit}
            onChange={(event) => onLimitChange(Number(event.target.value))}
            sx={{ minWidth: 88 }}
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      {queryError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {queryError?.data?.error || queryError?.message || "Query failed"}
        </Alert>
      ) : null}

      <Paper sx={{ overflow: "hidden" }}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={isExecuting || !hasLoaded}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          sortingMode={isTeamQuery ? "client" : "server"}
          sortModel={sortModel}
          onSortModelChange={isTeamQuery ? undefined : onSortModelChange}
          rowHeight={52}
          localeText={{
            noRowsLabel: nameSearch.trim()
              ? LOADED_NAME_SEARCH_EMPTY
              : "No players",
          }}
          sx={getResultsGridSx()}
        />
      </Paper>
    </Box>
  );
};

export default QueryResultsPanel;
